import { FormEvent, useCallback, useEffect, useRef, useState } from "react";
import AlertRuleForm from "./AlertRuleForm";
import { Map as MLMap, Marker, NavigationControl } from "maplibre-gl";
import type { GeoJSONSource } from "maplibre-gl";
import { supabase, type Device } from "../../lib/supabase";
import { MAP_STYLE } from "../../lib/basemap";
import { circle, zoneSize } from "../../lib/geo";
import { ALERT_LABEL, channelsText, orgApi, ruleSummary, type AlertRule, type Zone } from "../../lib/org";
import { Drawer, Field, Notice } from "../admin/ui";

type DevOrg = Device & { org_id?: string };

/** Alerts for one object: list/add/edit rules, manage the company's zones. */
export default function AlertsDrawer({ device, userEmail, canEdit, onClose }: {
  device: DevOrg; userEmail: string; canEdit: boolean; onClose: () => void;
}) {
  const [org, setOrg] = useState<string | null>(device.org_id ?? null);
  const [rules, setRules] = useState<AlertRule[]>([]);
  const [zones, setZones] = useState<Zone[]>([]);
  const [edit, setEdit] = useState<AlertRule | "new" | null>(null);
  const [zoneEdit, setZoneEdit] = useState<Zone | "new" | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const load = useCallback(async () => {
    let o = org;
    if (!o) { const { data } = await supabase.from("devices").select("org_id").eq("id", device.id).maybeSingle(); o = data?.org_id ?? null; setOrg(o); }
    if (!o) return;
    const [{ data: r }, { data: z }] = await Promise.all([
      supabase.from("alert_rules").select("*").eq("org_id", o).or(`device_id.is.null,device_id.eq.${device.id}`).order("created_at"),
      supabase.from("geofences").select("*").or(`org_id.eq.${o},is_global.eq.true`).order("name"),
    ]);
    setRules((r ?? []) as AlertRule[]); setZones((z ?? []) as Zone[]);
  }, [org, device.id]);
  useEffect(() => { load(); }, [load]);

  async function toggle(r: AlertRule) {
    try { await orgApi("PATCH", `alert-rules/${r.id}`, { enabled: !r.enabled }); await load(); } catch (e: any) { setErr(e.message); }
  }
  async function remove(r: AlertRule) {
    if (!confirm("Delete this alert?")) return;
    try { await orgApi("DELETE", `alert-rules/${r.id}`); await load(); } catch (e: any) { setErr(e.message); }
  }

  if (zoneEdit && org) return <ZoneEditor org={org} zone={zoneEdit === "new" ? null : zoneEdit} center={device.state}
    onClose={() => setZoneEdit(null)} onSaved={async () => { setZoneEdit(null); await load(); }} />;
  if (edit && org) return <AlertRuleForm org={org} device={device} rule={edit === "new" ? null : edit} zones={zones} userEmail={userEmail}
    onNewZone={() => { setEdit(null); setZoneEdit("new"); }} onClose={() => setEdit(null)} onSaved={async () => { setEdit(null); await load(); }} />;

  return (
    <Drawer title={`Alerts · ${device.name}`} onClose={onClose}
      footer={canEdit ? <><button className="btn-ghost" onClick={() => setZoneEdit("new")}>+ Zone</button><button className="btn-primary" onClick={() => setEdit("new")}>+ Add alert</button></> : undefined}>
      {!canEdit && <Notice kind="warn">Only the company owner or Skyward can change alerts. You'll still see them in the Events tab.</Notice>}
      {err && <Notice kind="bad" onClose={() => setErr(null)}>{err}</Notice>}
      <ul className="rule-list">
        {rules.map((r) => (
          <li key={r.id} className={r.enabled ? "" : "off"}>
            <div>
              <b>{r.name || ALERT_LABEL[r.type]}{r.is_default && <span className="tag">Default</span>}</b>
              <div className="sub">{ruleSummary(r, zones)} · {r.device_id ? "this object" : "all company objects"}</div>
              <div className="sub">Alert by: {channelsText(r)}</div>
            </div>
            {canEdit && <div className="rule-actions">
              <label className="switch" title={r.enabled ? "On" : "Off"}><input type="checkbox" checked={r.enabled} onChange={() => toggle(r)} /><span /></label>
              <button className="btn-ghost small" onClick={() => setEdit(r)}>Edit</button>
              <button className="btn-ghost small danger" onClick={() => remove(r)}>Delete</button>
            </div>}
          </li>
        ))}
        {!rules.length && <li className="sub">No alerts set up for this object yet.</li>}
      </ul>
      <h3 className="drawer-sub">Zones</h3>
      <ul className="rule-list">
        {zones.map((z) => (
          <li key={z.id}>
            <div><b><i className="zone-dot" style={{ background: z.color }} />{z.name}</b><div className="sub">{z.is_global ? "Global · " : ""}{zoneSize(z)}</div></div>
            {canEdit && !z.is_global && z.kind === "circle" && <div className="rule-actions"><button className="btn-ghost small" onClick={() => setZoneEdit(z)}>Edit</button>
              <button className="btn-ghost small danger" onClick={async () => { if (confirm(`Delete zone ${z.name}?`)) { try { await orgApi("DELETE", `geofences/${z.id}`); await load(); } catch (e: any) { setErr(e.message); } } }}>Delete</button></div>}
          </li>
        ))}
        {!zones.length && <li className="sub">No zones yet — e.g. depot, client site, no-go area.</li>}
      </ul>
    </Drawer>
  );
}

/** Pick a circular zone on a small map: click to place, set the radius. */
function ZoneEditor({ org, zone, center, onClose, onSaved }: {
  org: string; zone: Zone | null; center: Device["state"]; onClose: () => void; onSaved: () => void;
}) {
  const [name, setName] = useState(zone?.name ?? "");
  const [pt, setPt] = useState<[number, number] | null>(zone ? [zone.center_lng, zone.center_lat] : center?.lat != null ? [center.lng!, center.lat] : null);
  const [radius, setRadius] = useState(zone?.radius_m ?? 300);
  const [color, setColor] = useState(zone?.color ?? "#257AF4");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const el = useRef<HTMLDivElement>(null);
  const map = useRef<MLMap | null>(null);
  const mk = useRef<Marker | null>(null);

  useEffect(() => {
    if (!el.current) return;
    const m = new MLMap({ container: el.current, style: MAP_STYLE, center: pt ?? [178.0, -17.8], zoom: pt ? 14 : 7, attributionControl: { compact: true } });
    m.addControl(new NavigationControl({ visualizePitch: false }), "top-right");
    m.on("click", (e) => setPt([e.lngLat.lng, e.lngLat.lat]));
    m.on("load", () => {
      m.addSource("zone-edit", { type: "geojson", data: { type: "FeatureCollection", features: [] } });
      m.addLayer({ id: "zone-edit-fill", type: "fill", source: "zone-edit", paint: { "fill-color": color, "fill-opacity": 0.15 } });
      m.addLayer({ id: "zone-edit-line", type: "line", source: "zone-edit", paint: { "line-color": color, "line-width": 2 } });
      setPt((p) => (p ? [...p] as [number, number] : p)); // trigger draw
    });
    map.current = m;
    return () => { m.remove(); map.current = null; };
  }, []); // eslint-disable-line

  useEffect(() => {
    const m = map.current;
    if (!m || !pt) return;
    if (!mk.current) mk.current = new Marker({ color }).setLngLat(pt).addTo(m); else mk.current.setLngLat(pt);
    const src = m.getSource("zone-edit") as GeoJSONSource | undefined;
    src?.setData({ type: "FeatureCollection", features: [circle(pt, radius)] });
    if (m.getLayer("zone-edit-fill")) { m.setPaintProperty("zone-edit-fill", "fill-color", color); m.setPaintProperty("zone-edit-line", "line-color", color); }
  }, [pt, radius, color]);

  async function save(e: FormEvent) {
    e.preventDefault();
    if (!pt) return setErr("Click the map to place the zone.");
    setBusy(true); setErr(null);
    const body = { org_id: org, kind: "circle", name, center_lat: pt[1], center_lng: pt[0], radius_m: radius, color };
    try { if (zone) await orgApi("PATCH", `geofences/${zone.id}`, body); else await orgApi("POST", "geofences", body); onSaved(); }
    catch (x: any) { setErr(x.message); } finally { setBusy(false); }
  }

  return (
    <Drawer title={zone ? `Zone · ${zone.name}` : "New zone"} onClose={onClose}
      footer={<><button className="btn-ghost" onClick={onClose}>Back</button><button className="btn-primary" form="zone-form" disabled={busy}>{busy ? "Saving…" : "Save zone"}</button></>}>
      <form id="zone-form" className="aform" onSubmit={save}>
        <Field label="Zone name" wide><input value={name} onChange={(e) => setName(e.target.value)} required maxLength={60} placeholder="e.g. Nadi depot" /></Field>
        <div className="wide zone-map" ref={el} />
        <p className="wide sub" style={{ margin: 0 }}>Click the map to place the centre of the zone.</p>
        <Field label={`Radius: ${radius >= 1000 ? `${(radius / 1000).toFixed(1)} km` : `${radius} m`}`} wide>
          <input type="range" min={50} max={10000} step={50} value={radius} onChange={(e) => setRadius(Number(e.target.value))} />
        </Field>
        <Field label="Colour"><input type="color" value={color} onChange={(e) => setColor(e.target.value)} /></Field>
        {err && <div className="error wide">{err}</div>}
      </form>
    </Drawer>
  );
}

export { circle };
